import { type AxiosPromise } from "axios";
import { PaginatedResponse } from "pagination/model";

const getKeyFromType = (documentType: server.dto.DocumentTypes) => {
  switch (documentType) {
    case "contract": return "Contracts";
    case "invoice": return "Invoices";
    case "purchaseAgreement": return "PurchaseAgreements";
    case "quote": return "Quotes";
    default: throw new Error("Unknown document type: " + documentType);
  }
};

export const getDocumentApi = (documentType: server.dto.DocumentTypes) => {
  const key = getKeyFromType(documentType);
  return window.Api[key];
};

export const getDocumentCoreApi = (mainArea: server.dto.MainAreas) => {
  switch (mainArea) {
    case "contract": return window.CoreApi.Contract;
    case "purchaseAgreement": return window.CoreApi.PurchaseAgreement;
    case "quote": return window.CoreApi.Quote;
    default: throw new Error("Unsupported main area: " + mainArea);
  }
};

export const getDocumentUnlinkApi = (documentType: server.dto.DocumentTypes) => {
  return getDocumentApi(documentType).UnlinkShow;
};

// Sometimes we know for sure it's not an invoice
// Invoice api can be a bit different so this allows us to narrow our typings much better
export const getDocumentApiExceptInvoice = (documentType: server.dto.DocumentTypes) => {
  const key = getKeyFromType(documentType) as Exclude<ReturnType<typeof getKeyFromType>, "Invoices">;
  return window.Api[key];
};

export const getCopyEndpoint = (documentType: server.dto.DocumentTypes):
  ((documentId: number, data: server.dto.CopyDocumentInstruction) => AxiosPromise<server.dto.Document>) | ((invoiceId: number, data: server.dto.CopyInvoiceInstruction) => AxiosPromise<server.dto.Invoice>) => {
  const key = getKeyFromType(documentType);
  return window.Api[key].Copy;
};

export const getInvoiceV2EndpointFromLifecycle = (lifecycle: server.dto.DocumentLifeCycles) => {
  switch (lifecycle) {
    case "draft": return window.CoreApi.InvoiceV2.ChangeToDraftStage;
    case "review": return window.CoreApi.InvoiceV2.ChangeToReviewStage;
    case "pending": return window.CoreApi.InvoiceV2.ChangeToOutstanding;
  }
};

export const getESignEndpointFromLifecycle = (lifecycle: server.dto.DocumentLifeCycles) => {
  switch (lifecycle) {
    case "draft": return window.CoreApi.ESign.ChangeESignToDraftStage;
    case "review": return window.CoreApi.ESign.ChangeESignToReviewStage;
    case "pending": return window.CoreApi.ESign.ChangeESignToOutForSignature;
    case "discarded": return window.CoreApi.ESign.ChangeESignToDiscardedStage;
  }
};

export const emailApis: { [type in server.dto.MainAreas]?: {
  Api: {
    ListRelatedFiles: (itemId: number) => AxiosPromise<server.dto.RelatedFiles>
    GetEmails: (showId: number, page: number, per_page: number) => AxiosPromise<PaginatedResponse<server.dto.EmailMessage[]>>
    GetProcessedEmailTemplate: (dto: server.dto.GetProcessedEmailTemplateInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>
  }
}
} = {
  show: {
    Api: { ...window.Api.Shows, ...window.CoreApi.Show }
  },
  company: {
    Api: { ...window.Api.Companies, ...window.CoreApi.Company }
  },
  contact: {
    Api: { ...window.Api.Contacts, ...window.CoreApi.Contact }
  },
  contract: {
    Api: { ...window.Api.Contracts, ...window.CoreApi.Contract }
  },
  invoice: {
    Api: { ...window.Api.Invoices, ...window.CoreApi.Invoice }
  },
  purchaseAgreement: {
    Api: { ...window.Api.PurchaseAgreements, ...window.CoreApi.PurchaseAgreement }
  },
  quote: {
    Api: { ...window.Api.Quotes, ...window.CoreApi.Quote }
  }
};

export const downloadFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const apiResponse = {
  success: <T>(data: T): server.dto.ApiResponseWrapper<T> => ({ data, hasErrors: false, errors: [], pagination: null }),
  somethingWentWrong: <T extends any[]>(): server.dto.ApiResponseWrapper<T> => ({ data: [] as T, hasErrors: true, errors: [{ message: RESX.GeneralLabels.SomethingUnexpectedHappened, code: "unknown", target: null }], pagination: null }),
}